'use client';

import { useFormatter, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useStore } from '@/app/providers/store';
import type { ServiceError, TaskParams } from '@/app/types';
import { formatRelativeTime, type getDefaultReportParams, reportEvent } from '@/app/utils';
import { useGlobalModal } from './use-global-modal';
import { useMembershipRevision } from './use-membership-revision';
import { useQuota } from './use-quota';

export type ReportEventParams = ReturnType<typeof getDefaultReportParams>;

type SubmitTaskErrorType =
  | 'unknownError'
  | 'creditsInsufficient'
  | 'creditsInsufficientForSubs'
  | 'SubsplanNotSupportedForTask'
  | 'SubsplanNotSupportedForTaskForSubs'
  | 'checkFailed'
  | 'checkFailedExceeded'
  | 'checkFailedExceededForSubs'
  | 'systemThrottling'
  | 'systemThrottlingForSubs'
  | 'fieldInvalid'
  | 'elementDeleted'
  | 'extendCountExceeded'
  | 'nonPeakTaskSubmitLimitExceeded'
  | 'nonPeakTaskSubmitLimitExceededNoCoupon'
  | 'nopeakQuotaUnavailable'
  | 'teamCreditsInsufficientContactAdmin'
  | 'teamNonPeakTaskSubmitLimitExceeded';

interface UseDialogsProps {
  isFree: boolean;
  templateScene: string;
  taskType: TaskParams['type'];
  onCloseDialog?: () => void;
  onImagePromoCreationShortage?: () => void;
}

export const useTaskSubmitError = ({
  isFree,
  templateScene,
  taskType,
  onCloseDialog,
  onImagePromoCreationShortage,
}: UseDialogsProps) => {
  const { availableCoupons, isTeamSpace, nopeakQuota } = useStore((state) => ({
    availableCoupons: state.availableCoupons,
    isTeamSpace: state.isTeamSpace,
    nopeakQuota: state.userQuota?.nopeak_quota,
  }));
  const format = useFormatter();
  const t = useTranslations();
  const { open: openSubmitErrorDialog, close: closeSubmitErrorDialog } = useGlobalModal('submitErrorDialog');
  const { nopeakDailyLimit } = useQuota();
  const isNewMembership = useMembershipRevision();
  const nopeakQuotaUnavailable = isNewMembership && !isTeamSpace && nopeakQuota?.total === 0;

  const handleError = (error: ServiceError, reportEventParams: ReportEventParams) => {
    let showType = 'dialog';
    let key: SubmitTaskErrorType = 'unknownError';
    let toastKey:
      | 'taskCountExceeded'
      | 'tooManyRequests'
      | 'imageGenerationServiceBusy'
      | 'libImageInvalid'
      | 'libImageViolate'
      | 'libImageDownloadFailed'
      | 'imagePromoCreationShortage'
      | 'imagePromoCreationExceed' = 'taskCountExceeded';
    let showPayment = false;
    let showOk = true;
    let showPayCreditsEntry = false;
    const descriptionValues: { time?: string; feature?: string; limit?: string } = {};
    switch (error.reason) {
      case 'CreditInsufficient':
        if (isTeamSpace) {
          key = 'teamCreditsInsufficientContactAdmin';
        } else if (isFree && !isNewMembership) {
          key = 'creditsInsufficient';
          showPayment = true;
          showOk = false;
        } else {
          key = 'creditsInsufficientForSubs';
          if (isFree && isNewMembership) {
            showPayCreditsEntry = true;
            showOk = false;
          }
        }
        break;
      case 'SubsplanNotSupportedForTask':
        if (error.metadata?.duration) {
          descriptionValues.feature = t('create.createForm.settings.duration.option.text', {
            time: error.metadata?.duration,
          });
        } else if (error.metadata?.resolution) {
          descriptionValues.feature = error.metadata?.resolution;
        } else if (error.metadata?.schedule_mode === 'nopeak') {
          if (nopeakQuotaUnavailable) {
            key = 'nopeakQuotaUnavailable';
            break;
          }
          descriptionValues.feature = t('create.createForm.settings.nonPeakMode.title');
        } else if (error.metadata?.samplecount) {
          descriptionValues.feature = t('create.createForm.settings.amount.feature');
        } else if (error.metadata?.transition && error.metadata?.transition === 'pro') {
          descriptionValues.feature = t('create.createForm.settings.creationMode.optionCinematic.text');
        } else if (error.metadata?.codec) {
          descriptionValues.feature = t('create.createForm.settings.codec.title');
        } else {
          descriptionValues.feature = t('popup.dialog.error.SubsplanNotSupportedForTask.defaultFeature');
        }

        if (isFree || error.metadata?.subsplan === 'free') {
          key = 'SubsplanNotSupportedForTask';
          if (error.metadata?.type === 'video2extend') {
            descriptionValues.feature = t('create.extendForm.title');
          }
          showPayment = true;
          showOk = false;
        } else {
          key = 'SubsplanNotSupportedForTaskForSubs';
        }
        break;
      // 单次审核任务失败
      case 'TaskPromptPolicyViolation':
      case 'AuditSubmitIllegal':
      case 'SensitiveContentDetected': // 老罗敏感词过滤
        key = 'checkFailed';
        break;
      // 多次审核任务失败
      case 'TaskPromptPolicyExcessiveViolations':
      case 'AuditSubmitSuspended':
        key = 'checkFailedExceeded';
        if (error.metadata?.end_time) {
          descriptionValues.time = formatRelativeTime(new Date(error.metadata.end_time), format.relativeTime);
        }
        break;
      // 多次审核任务失败，达到警告阈值
      case 'AuditViolationWarning':
        if (isFree) {
          key = 'checkFailedExceeded';
        } else {
          key = 'checkFailedExceededForSubs';
        }
        if (error.metadata?.end_time) {
          descriptionValues.time = formatRelativeTime(new Date(error.metadata.end_time), format.relativeTime);
        }
        break;
      case 'TaskVideoTooLongToExtend':
        key = 'extendCountExceeded';
        break;
      case 'QuotaExceeded':
        showType = 'toast';
        toastKey = 'taskCountExceeded';
        break;
      case 'TooManyRequests':
        showType = 'toast';
        toastKey = 'tooManyRequests';
        break;
      case 'TSKIGRImageGenerationServiceBusy':
        showType = 'toast';
        toastKey = 'imageGenerationServiceBusy';
        break;
      case 'TSKIGRLibModelInputImageInvalid':
        showType = 'toast';
        toastKey = 'libImageInvalid';
        break;
      case 'TSKIGRLibModelInputImageViolate':
        showType = 'toast';
        toastKey = 'libImageViolate';
        break;
      case 'TSKIGRLibModelInputDownlowdFailed':
        showType = 'toast';
        toastKey = 'libImageDownloadFailed';
        break;
      case 'ImagePromoCreationShortage':
        showType = 'toast';
        toastKey = 'imagePromoCreationShortage';
        onImagePromoCreationShortage?.();
        break;
      case 'ImagePromoCreationExceed':
        showType = 'toast';
        toastKey = 'imagePromoCreationExceed';
        break;
      case 'SystemThrottling':
        if (isFree) {
          key = 'systemThrottling';
          showPayment = true;
          showOk = false;
        } else {
          key = 'systemThrottlingForSubs';
        }
        break;
      case 'FieldInvalid':
        key = 'fieldInvalid';
        break;
      case 'ElementNotFound':
        key = 'elementDeleted';
        break;
      case 'NonPeakTaskSubmitLimitExceeded':
        if (isTeamSpace) {
          key = 'teamNonPeakTaskSubmitLimitExceeded';
        } else if (nopeakQuotaUnavailable) {
          key = 'nopeakQuotaUnavailable';
        } else {
          key = availableCoupons.includes('ultimate_v2_off_peak_coupon')
            ? 'nonPeakTaskSubmitLimitExceeded'
            : 'nonPeakTaskSubmitLimitExceededNoCoupon';
          showOk = false;
          showPayCreditsEntry = true;
        }
        descriptionValues.limit = String(
          isNewMembership && !isTeamSpace ? (nopeakQuota?.total ?? 0) : nopeakDailyLimit,
        );
        break;
    }

    if (showType === 'dialog') {
      openSubmitErrorDialog({
        title: t(`popup.dialog.error.${key}.title`),
        description:
          key === 'checkFailed'
            ? `${t('popup.dialog.error.checkFailed.content')}${error.metadata?.audit_id ? `\n(ID: ${error.metadata?.audit_id})` : ''}`
            : (t.rich(`popup.dialog.error.${key}.content`, {
                ul: (parts) => <ul className="list-disc pl-6">{parts}</ul>,
                li: (parts) => parts && <li key={parts.toString()}>{parts}</li>,
                ...descriptionValues,
              }) as ReactNode),
        showPayment,
        showOk,
        showPayCreditsEntry,
        templateScene,
        taskType,
        onClose: onCloseDialog,
      });
    } else {
      toast({
        type: 'error',
        message: t(`popup.toast.${toastKey}`),
        duration: 5000,
      });
    }

    if (showPayment) {
      reportEvent('vip_price_preview_exposure', {
        from_page: `${templateScene ? 'template -' : ''} create button dialog (${
          key === 'unknownError' ? error.reason : key
        })`,
      });
    }
    reportEvent('create_video_click', {
      ...reportEventParams,
      is_success: false,
      fail_reason: key === 'unknownError' ? error.reason : key,
    });
  };

  const handleDialogOpenChange = (open?: boolean) => {
    if (!open) {
      closeSubmitErrorDialog();
      onCloseDialog?.();
    }
  };

  return {
    handleError,
    handleDialogOpenChange,
  };
};
